import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  CircleStackIcon,
  PlusIcon,
  TrashIcon,
  KeyIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

import apiService from '../services/api';
import { S3Bucket, S3AccessKey, S3AccessKeySecret } from '../types/api';
import { useOrganisation } from '../contexts/OrganisationContext';
import Logger from '../utils/logger';

import CreateBucketModal from '../components/NubiS3/CreateBucketModal';
import CreateAccessKeyModal from '../components/NubiS3/CreateAccessKeyModal';
import RevealSecretOnceModal from '../components/NubiS3/RevealSecretOnceModal';
import ObjectsExplorer from '../components/NubiS3/ObjectsExplorer';

const formatBytes = (bytes: number): string => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(i > 0 ? 2 : 0)} ${sizes[i]}`;
};

const formatDate = (iso: string): string =>
  new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso));

const NubiS3Page: React.FC = () => {
  const { t } = (useTranslation as any)('nubis3');
  const { currentOrganisation } = useOrganisation();
  const [buckets, setBuckets] = useState<S3Bucket[]>([]);
  const [loading, setLoading] = useState(true);
  const [createBucketOpen, setCreateBucketOpen] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState<S3Bucket | null>(null);

  const loadBuckets = useCallback(async () => {
    if (!currentOrganisation) return;
    try {
      setLoading(true);
      const data = await apiService.listS3Buckets(currentOrganisation.id);
      setBuckets(data);
    } catch (err) {
      Logger.error('Load S3 buckets failed', err);
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  }, [currentOrganisation, t]);

  useEffect(() => { loadBuckets(); }, [loadBuckets]);

  const handleBucketCreated = (b: S3Bucket) => {
    setBuckets((prev) => [b, ...prev]);
  };

  const handleDeleteBucket = async (bucket: S3Bucket) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(t('bucket.delete.title', { name: bucket.name }) + '\n\n' + t('bucket.delete.warning'))) return;
    try {
      await apiService.deleteS3Bucket(bucket.id);
      setBuckets((prev) => prev.filter((b) => b.id !== bucket.id));
      if (selectedBucket?.id === bucket.id) setSelectedBucket(null);
      toast.success(t('bucket.delete.success'));
    } catch (err) {
      Logger.error('Delete S3 bucket failed', err);
      toast.error(t('bucket.delete.errorDefault'));
    }
  };

  if (!currentOrganisation) {
    return (
      <div className="flex items-center justify-center py-20 text-text-secondary">
        {t('selectOrganisation', { ns: 'common', defaultValue: 'Sélectionne une organisation' })}
      </div>
    );
  }

  if (selectedBucket) {
    return (
      <BucketDetailPanel
        bucket={selectedBucket}
        onBack={() => setSelectedBucket(null)}
        onDeleted={() => {
          setBuckets((prev) => prev.filter((b) => b.id !== selectedBucket.id));
          setSelectedBucket(null);
        }}
      />
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <CircleStackIcon className="w-6 h-6 text-accent-500" />
            {t('title')}
          </h1>
          <p className="text-sm text-text-secondary mt-1">{t('subtitle')}</p>
          {!loading && buckets.length > 0 && (
            <p className="text-xs text-text-secondary mt-2">
              {t('header.bucketCount', { count: buckets.length })}
            </p>
          )}
        </div>
        {buckets.length > 0 && (
          <button
            type="button"
            onClick={() => setCreateBucketOpen(true)}
            className="px-4 py-2 rounded-md bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 flex items-center gap-2"
          >
            <PlusIcon className="w-4 h-4" /> {t('header.createBucket')}
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent-500" />
        </div>
      ) : buckets.length === 0 ? (
        <div className="bg-surface-1 rounded-lg p-12 text-center border border-border-1">
          <CircleStackIcon className="w-12 h-12 text-text-secondary mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-text-primary mb-2">{t('empty.title')}</h2>
          <p className="text-sm text-text-secondary mb-6 max-w-md mx-auto">{t('empty.description')}</p>
          <button
            type="button"
            onClick={() => setCreateBucketOpen(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-white text-sm font-medium hover:bg-accent-600"
          >
            <PlusIcon className="w-4 h-4" /> {t('empty.cta')}
          </button>
        </div>
      ) : (
        <div className="bg-surface-1 rounded-lg border border-border-1 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-text-secondary text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left font-medium">{t('bucket.name')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('bucket.used')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('bucket.region')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('bucket.createdAt')}</th>
                <th className="px-4 py-3 text-right font-medium">{t('accessKeys.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-1">
              {buckets.map((b) => (
                <tr key={b.id} className="hover:bg-surface-2/50">
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setSelectedBucket(b)}
                      className="text-text-primary font-medium hover:text-accent-500"
                    >
                      {b.name}
                    </button>
                    {b.description && (
                      <div className="text-xs text-text-secondary mt-0.5">{b.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-text-secondary">
                    {formatBytes(b.used_bytes)} · {t('bucket.objects', { count: b.object_count })}
                  </td>
                  <td className="px-4 py-3 text-text-secondary">{b.region}</td>
                  <td className="px-4 py-3 text-text-secondary">{formatDate(b.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => setSelectedBucket(b)}
                      className="px-2 py-1 text-xs font-medium text-accent-500 hover:bg-accent-500/10 rounded mr-1"
                    >
                      {t('bucket.actions.view')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteBucket(b)}
                      className="px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-500/10 rounded"
                      aria-label="delete"
                    >
                      <TrashIcon className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateBucketModal
        organisationId={currentOrganisation.id}
        isOpen={createBucketOpen}
        onClose={() => setCreateBucketOpen(false)}
        onCreated={handleBucketCreated}
      />
    </div>
  );
};

// =====================================================
// Bucket detail panel (with Access Keys tab)
// =====================================================

interface BucketDetailPanelProps {
  bucket: S3Bucket;
  onBack: () => void;
  onDeleted: () => void;
}

type DetailTab = 'files' | 'keys';

const BucketDetailPanel: React.FC<BucketDetailPanelProps> = ({ bucket, onBack, onDeleted }) => {
  const { t } = (useTranslation as any)('nubis3');
  const [activeTab, setActiveTab] = useState<DetailTab>('files');
  const [keys, setKeys] = useState<S3AccessKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [createKeyOpen, setCreateKeyOpen] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<S3AccessKeySecret | null>(null);

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.listS3AccessKeys(bucket.id);
      setKeys(data);
    } catch (err) {
      Logger.error('Load access keys failed', err);
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  }, [bucket.id, t]);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const handleKeyCreated = (secret: S3AccessKeySecret) => {
    setRevealedSecret(secret);
    // Refresh la liste pour faire apparaître la nouvelle clé (sans le secret).
    loadKeys();
  };

  const handleRevokeKey = async (key: S3AccessKey) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(t('accessKeys.revoke.title', { name: key.name }) + '\n\n' + t('accessKeys.revoke.warning'))) return;
    try {
      await apiService.revokeS3AccessKey(bucket.id, key.id);
      setKeys((prev) => prev.filter((k) => k.id !== key.id));
      toast.success(t('accessKeys.revoke.success'));
    } catch (err) {
      Logger.error('Revoke key failed', err);
      toast.error(t('accessKeys.revoke.errorDefault'));
    }
  };

  const handleDeleteBucket = async () => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(t('bucket.delete.title', { name: bucket.name }) + '\n\n' + t('bucket.delete.warning'))) return;
    try {
      await apiService.deleteS3Bucket(bucket.id);
      toast.success(t('bucket.delete.success'));
      onDeleted();
    } catch (err) {
      Logger.error('Delete bucket failed', err);
      toast.error(t('bucket.delete.errorDefault'));
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary mb-4"
      >
        <ArrowLeftIcon className="w-4 h-4" /> {t('back', { ns: 'common', defaultValue: 'Retour' })}
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <CircleStackIcon className="w-6 h-6 text-accent-500" /> {bucket.name}
          </h1>
          {bucket.description && (
            <p className="text-sm text-text-secondary mt-1">{bucket.description}</p>
          )}
        </div>
        <button
          type="button"
          onClick={handleDeleteBucket}
          className="px-3 py-2 rounded-md text-sm font-medium text-red-500 hover:bg-red-500/10 border border-red-500/30 flex items-center gap-2"
        >
          <TrashIcon className="w-4 h-4" /> {t('bucket.actions.delete')}
        </button>
      </div>

      {/* Bucket meta card */}
      <div className="bg-surface-1 rounded-lg border border-border-1 p-5 mb-6">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div>
            <dt className="text-xs text-text-secondary mb-0.5">{t('bucket.endpoint')}</dt>
            <dd className="text-text-primary font-mono text-xs break-all">{bucket.endpoint}</dd>
          </div>
          <div>
            <dt className="text-xs text-text-secondary mb-0.5">{t('bucket.b2Name')}</dt>
            <dd className="text-text-primary font-mono text-xs break-all">{bucket.b2_bucket_name}</dd>
          </div>
          <div>
            <dt className="text-xs text-text-secondary mb-0.5">{t('bucket.region')}</dt>
            <dd className="text-text-primary">{bucket.region}</dd>
          </div>
          <div>
            <dt className="text-xs text-text-secondary mb-0.5">{t('bucket.used')}</dt>
            <dd className="text-text-primary">
              {formatBytes(bucket.used_bytes)} · {t('bucket.objects', { count: bucket.object_count })}
              {bucket.quota_bytes && (
                <span className="text-text-secondary"> / {formatBytes(bucket.quota_bytes)}</span>
              )}
            </dd>
          </div>
        </dl>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border-1 mb-4">
        <button
          type="button"
          onClick={() => setActiveTab('files')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'files'
              ? 'border-accent-500 text-accent-500'
              : 'border-transparent text-text-secondary hover:text-text-primary'
          }`}
        >
          {t('files.title')}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('keys')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'keys'
              ? 'border-accent-500 text-accent-500'
              : 'border-transparent text-text-secondary hover:text-text-primary'
          }`}
        >
          {t('accessKeys.title')}
        </button>
      </div>

      {/* Files tab */}
      {activeTab === 'files' && (
        <ObjectsExplorer bucketId={bucket.id} />
      )}

      {/* Access keys tab */}
      {activeTab === 'keys' && (
        <div className="bg-surface-1 rounded-lg border border-border-1">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border-1">
            <div>
              <h2 className="text-base font-semibold text-text-primary flex items-center gap-2">
                <KeyIcon className="w-5 h-5 text-accent-500" /> {t('accessKeys.title')}
              </h2>
              <p className="text-xs text-text-secondary mt-0.5">{t('accessKeys.subtitle')}</p>
            </div>
            <button
              type="button"
              onClick={() => setCreateKeyOpen(true)}
              className="px-3 py-2 rounded-md bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 flex items-center gap-2"
            >
              <PlusIcon className="w-4 h-4" /> {t('accessKeys.create')}
            </button>
          </div>

          {loading ? (
            <div className="p-10 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-500" />
            </div>
          ) : keys.length === 0 ? (
            <div className="p-10 text-center text-sm text-text-secondary">
              <KeyIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
              {t('accessKeys.empty')}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-surface-2 text-text-secondary text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">{t('accessKeys.table.name')}</th>
                  <th className="px-4 py-2 text-left font-medium">{t('accessKeys.table.keyId')}</th>
                  <th className="px-4 py-2 text-left font-medium">{t('accessKeys.table.capabilities')}</th>
                  <th className="px-4 py-2 text-left font-medium">{t('accessKeys.table.createdAt')}</th>
                  <th className="px-4 py-2 text-left font-medium">{t('accessKeys.table.lastUsed')}</th>
                  <th className="px-4 py-2 text-right font-medium">{t('accessKeys.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-1">
                {keys.map((k) => (
                  <tr key={k.id} className="hover:bg-surface-2/50">
                    <td className="px-4 py-3 text-text-primary font-medium">{k.name}</td>
                    <td className="px-4 py-3 text-text-secondary font-mono text-xs">{k.b2_key_id}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {k.capabilities.slice(0, 3).map((c) => (
                          <span key={c} className="px-1.5 py-0.5 rounded text-xs bg-surface-2 text-text-secondary">
                            {c}
                          </span>
                        ))}
                        {k.capabilities.length > 3 && (
                          <span className="px-1.5 py-0.5 rounded text-xs bg-surface-2 text-text-secondary">
                            +{k.capabilities.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-text-secondary text-xs">{formatDate(k.created_at)}</td>
                    <td className="px-4 py-3 text-text-secondary text-xs">
                      {k.last_used_at ? formatDate(k.last_used_at) : t('accessKeys.table.never')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleRevokeKey(k)}
                        className="px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-500/10 rounded"
                      >
                        <TrashIcon className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <CreateAccessKeyModal
        bucketId={bucket.id}
        isOpen={createKeyOpen}
        onClose={() => setCreateKeyOpen(false)}
        onCreated={handleKeyCreated}
      />

      {revealedSecret && (
        <RevealSecretOnceModal secret={revealedSecret} onClose={() => setRevealedSecret(null)} />
      )}
    </div>
  );
};

export default NubiS3Page;
